// Joyride 2.x no posiciona bien el spotlight de un target que tiene un padre con scroll dentro de
// un elemento fixed (el cuerpo de un MUI Dialog): usa offsetTop (relativo al Paper) en vez del
// viewport. Y con disableScrollParentFix=false "lo arregla" poniendo overflow: initial en ese padre,
// lo que rompe el scroll del modal en los pasos siguientes.
// Solución: cada paso apunta a un proxy fixed colgado de body (sin padres con scroll) que copia la
// parte visible del target real. El scroll hasta el target lo hacemos nosotros.

export const TUTORIAL_PROXY_ATTR = "data-tutorial-proxy";
export const TUTORIAL_PROXY_SOURCE_ATTR = "data-tutorial-proxy-source";

const SCROLLABLE_OVERFLOW = /(auto|scroll)/;
const CLIPPING_OVERFLOW = /(auto|scroll|hidden|clip)/;
const REVEAL_PADDING = 16;
const PROXY_PROPS = ["display", "top", "left", "width", "height"];

/** Sustituye el target de cada paso por su proxy. El original queda en `tutorialSourceTarget`. */
export const withTutorialProxyTargets = (steps, scope) =>
  steps.map((step, index) => ({
    ...step,
    target: `[${TUTORIAL_PROXY_ATTR}="${scope}-${index}"]`,
    tutorialProxyKey: `${scope}-${index}`,
    tutorialSourceTarget: step.target,
  }));

const queryElement = (selector) => {
  if (!selector) return null;
  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
};

// Rect del elemento recortado por los ancestros que lo ocultan y por el viewport.
const getVisibleRect = (element) => {
  const rect = element.getBoundingClientRect();
  let { top, left, right, bottom } = rect;

  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (CLIPPING_OVERFLOW.test(`${style.overflow}${style.overflowX}${style.overflowY}`)) {
      const box = node.getBoundingClientRect();
      top = Math.max(top, box.top);
      left = Math.max(left, box.left);
      right = Math.min(right, box.right);
      bottom = Math.min(bottom, box.bottom);
    }
    if (style.position === "fixed") break;
  }

  top = Math.max(top, 0);
  left = Math.max(left, 0);
  right = Math.min(right, window.innerWidth);
  bottom = Math.min(bottom, window.innerHeight);

  return { top, left, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
};

/** Copia en cada proxy montado el rect visible de su target. Devuelve true si alguno cambió. */
export const syncTutorialProxies = () => {
  let changed = false;
  document.querySelectorAll(`[${TUTORIAL_PROXY_ATTR}]`).forEach((proxy) => {
    const source = queryElement(proxy.getAttribute(TUTORIAL_PROXY_SOURCE_ATTR));
    let next = { display: "none" };
    if (source) {
      const rect = getVisibleRect(source);
      next = {
        display: "block",
        top: `${Math.round(rect.top)}px`,
        left: `${Math.round(rect.left)}px`,
        width: `${Math.round(rect.width)}px`,
        height: `${Math.round(rect.height)}px`,
      };
    }
    PROXY_PROPS.forEach((prop) => {
      if (next[prop] !== undefined && proxy.style[prop] !== next[prop]) {
        proxy.style[prop] = next[prop];
        changed = true;
      }
    });
  });
  return changed;
};

const scrollAxisIntoView = (container, rect, box, axis) => {
  const [start, end, size, scrollProp] =
    axis === "y" ? ["top", "bottom", "height", "scrollTop"] : ["left", "right", "width", "scrollLeft"];
  if (rect[size] > box[size] || rect[start] < box[start]) {
    container[scrollProp] += rect[start] - box[start] - REVEAL_PADDING;
  } else if (rect[end] > box[end]) {
    container[scrollProp] += rect[end] - box[end] + REVEAL_PADDING;
  }
};

/** Hace scroll (sin animación) en los contenedores necesarios para que el target quede visible. */
export const revealTutorialTarget = (selector) => {
  const element = queryElement(selector);
  if (!element) return;

  let insideFixed = false;
  for (let node = element.parentElement; node && node !== document.body; node = node.parentElement) {
    const style = getComputedStyle(node);
    const canScrollY = SCROLLABLE_OVERFLOW.test(style.overflowY) && node.scrollHeight > node.clientHeight;
    const canScrollX = SCROLLABLE_OVERFLOW.test(style.overflowX) && node.scrollWidth > node.clientWidth;
    if (canScrollY || canScrollX) {
      const box = node.getBoundingClientRect();
      if (canScrollY) scrollAxisIntoView(node, element.getBoundingClientRect(), box, "y");
      if (canScrollX) scrollAxisIntoView(node, element.getBoundingClientRect(), box, "x");
    }
    if (style.position === "fixed") {
      insideFixed = true;
      break;
    }
  }

  if (!insideFixed) {
    const viewport = { top: 0, left: 0, bottom: window.innerHeight, right: window.innerWidth };
    viewport.height = viewport.bottom;
    viewport.width = viewport.right;
    const scroller = document.scrollingElement || document.documentElement;
    scrollAxisIntoView(scroller, element.getBoundingClientRect(), viewport, "y");
  }
};

/**
 * Espera a que el target esté montado, con tamaño y quieto (fin de transiciones de modal/menú),
 * lo hace visible, sincroniza los proxies y llama a onReady. Devuelve la función de cancelación.
 */
export const runWhenTutorialTargetReady = (
  selector,
  onReady,
  { timeout = 4000, onTimeout } = {}
) => {
  let frame = null;
  let cancelled = false;
  let lastKey = null;
  let stableFrames = 0;
  const startedAt = performance.now();

  const check = () => {
    if (cancelled) return;
    const element = queryElement(selector);
    if (element) {
      const rect = element.getBoundingClientRect();
      const key = `${rect.top}|${rect.left}|${rect.width}|${rect.height}`;
      stableFrames = rect.width > 0 && rect.height > 0 && key === lastKey ? stableFrames + 1 : 0;
      lastKey = key;
      if (stableFrames >= 2) {
        revealTutorialTarget(selector);
        syncTutorialProxies();
        onReady();
        return;
      }
    }
    if (performance.now() - startedAt > timeout) {
      (onTimeout || onReady)();
      return;
    }
    frame = requestAnimationFrame(check);
  };

  frame = requestAnimationFrame(check);
  return () => {
    cancelled = true;
    if (frame !== null) cancelAnimationFrame(frame);
  };
};

/** Prepara el paso `index` antes de mostrarlo: scroll hasta su target y proxies al día. */
export const prepareTutorialStep = (steps, index) => {
  const step = steps[index];
  if (!step) return;
  revealTutorialTarget(step.tutorialSourceTarget || step.target);
  syncTutorialProxies();
};
